import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// Session-wide data every shell/screen needs: `/me`, branches, feature entitlements. Loaded once
// at the shell level (not re-fetched per-page) — UX-01 §2 "scope every call by session
// organization_id" is enforced server-side already (TenantGuard); this context just avoids N
// duplicate /me calls across sibling screens.
const AppDataContext = createContext(null);

export function AppDataProvider({ children }) {
  const [me, setMe] = useState(null);
  const [branches, setBranches] = useState([]);
  const [entitlements, setEntitlements] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readOnlyLapse, setReadOnlyLapse] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState("all");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await api.get("/me");
      if (meRes.data.user_type === "kairoo") {
        navigate("/kairoo/dashboard");
        return;
      }
      // UX-FD-01/03: Front Desk AND Manager-tier staff never render the org-wide Owner Cockpit
      // shell — bounce a stray /app/* visit (stale bookmark, back-button) to the Desk shell
      // instead (UX-FD-03 §1: "a Manager operates one branch, not the org" — org-level screens
      // stay Super-Admin-only, on this shell).
      if (["front_of_house_fd", "front_of_house_mgr"].includes(meRes.data.role)) {
        navigate("/desk/today");
        return;
      }
      // UX-TR-01 §0: a trainer login previously fell through to here and rendered the
      // Super-Admin Owner Cockpit shell — trainer now has its own persona surface.
      if (meRes.data.role === "trainer") {
        navigate("/trainer/today");
        return;
      }
      const [branchRes, entRes] = await Promise.all([
        api.get("/branches"),
        api.get("/feature-entitlements"),
      ]);
      setMe(meRes.data);
      setBranches(branchRes.data.branches);
      setEntitlements(entRes.data.entitlements);
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/login");
        return;
      }
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Reactive read-only-lapse detection (see api.js interceptor comment) — no proactive endpoint
  // exists for an org to check its own license state, so this only lights up once a real write
  // has actually been blocked by the server this session.
  useEffect(() => {
    const handler = () => setReadOnlyLapse(true);
    window.addEventListener("fitfrik:read-only-lapse", handler);
    return () => window.removeEventListener("fitfrik:read-only-lapse", handler);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    navigate("/login");
  }, [navigate]);

  return (
    <AppDataContext.Provider value={{
      me, branches, entitlements, loading, error, reload: load, readOnlyLapse, logout,
      selectedBranchId, setSelectedBranchId,
    }}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  return useContext(AppDataContext);
}
